export type Condition = { name: string; description?: string };

export type Volume = {
  numeral: string;
  title: string;
  headline: string;
  intro: string[];
  colorClass: string;
  listLabel: string;
  items: string[];
  conditions?: Condition[];
  result: string;
};

export const volumes: Volume[] = [
  {
    numeral: "I",
    title: "The 15-Minute Alopecia Assessment Framework™",
    headline: "Organize Your Initial Hair Loss Assessment Through 5 Structured Stages.",
    intro: [
      "This is the foundation of the entire system.",
      "Inside, you’ll explore a practical educational framework designed to organize the initial review of relevant hair loss information.",
    ],
    colorClass: "bg-vol-1",
    listLabel: "What you’ll learn",
    items: [
      "The five-stage assessment sequence.",
      "Patient history review frameworks.",
      "Hair loss pattern documentation.",
      "Scalp examination considerations.",
      "Relevant clinical observations.",
      "Assessment organization worksheets.",
      "Initial review checklists.",
      "Documentation of missing information.",
      "Professional escalation considerations.",
    ],
    result:
      "A repeatable framework for completing a structured initial review worksheet, with a target workflow of 15 minutes when the necessary information is available.",
  },
  {
    numeral: "II",
    title: "The Trichoscopy Clinical Atlas™",
    headline: "Study Essential Trichoscopic Findings Through Organized Visual References.",
    intro: [
      "Trichoscopy plays an important role in the evaluation of hair and scalp disorders.",
      "This volume organizes selected findings into a structured educational atlas.",
    ],
    colorClass: "bg-vol-2",
    listLabel: "What you’ll learn",
    items: [
      "Hair shaft diameter diversity.",
      "Yellow dots.",
      "Black dots.",
      "Exclamation mark hairs.",
      "Broken hairs.",
      "Short regrowing hairs.",
      "Follicular opening characteristics.",
      "Perifollicular scaling.",
      "Perifollicular erythema.",
      "Scalp vascular patterns.",
      "Clinical context and interpretation considerations.",
    ],
    result: "A more organized visual reference for reviewing important trichoscopic features.",
  },
  {
    numeral: "III",
    title: "The Non-Scarring Alopecia Clinical Roadmap™",
    headline: "Learn to Compare 6 Important Non-Scarring Hair Loss Presentations.",
    intro: [
      "Not all non-scarring alopecias share the same characteristics.",
      "This volume organizes six important conditions into a structured educational comparison system.",
    ],
    colorClass: "bg-vol-3",
    conditions: [
      {
        name: "Androgenetic Alopecia",
        description:
          "Review patterned hair loss, follicular miniaturization concepts, and relevant clinical observations.",
      },
      {
        name: "Alopecia Areata",
        description:
          "Study characteristic presentations, clinical variation, and selected trichoscopic findings.",
      },
      {
        name: "Telogen Effluvium",
        description:
          "Explore diffuse shedding presentations, relevant history, and differential diagnosis considerations.",
      },
      {
        name: "Anagen Effluvium",
        description:
          "Review educational references concerning abrupt hair loss associated with disruption of anagen hair growth.",
      },
      {
        name: "Traction Alopecia",
        description: "Study hair loss associated with mechanical tension and relevant clinical patterns.",
      },
      {
        name: "Trichotillomania",
        description: "Review characteristic hair shaft changes and clinical presentation considerations.",
      },
    ],
    listLabel: "Additional resources",
    items: [
      "Clinical comparison tables.",
      "Pattern recognition worksheets.",
      "Trichoscopy reference summaries.",
      "Educational case discussions.",
      "Differential diagnosis questions.",
    ],
    result:
      "A structured educational resource for comparing six non-scarring alopecia presentations.",
  },
  {
    numeral: "IV",
    title: "The Scarring Alopecia Clinical Atlas™",
    headline: "Study 6 Important Cicatricial Alopecia Conditions Through Structured Clinical References.",
    intro: [
      "Scarring alopecias can involve inflammatory processes that damage hair follicles.",
      "Recognizing concerning presentations and understanding the need for appropriate professional evaluation is essential.",
    ],
    colorClass: "bg-vol-4",
    conditions: [
      { name: "Lichen Planopilaris" },
      { name: "Frontal Fibrosing Alopecia" },
      { name: "Central Centrifugal Cicatricial Alopecia" },
      { name: "Discoid Lupus Erythematosus Affecting the Scalp" },
      { name: "Folliculitis Decalvans" },
      { name: "Dissecting Cellulitis of the Scalp" },
    ],
    listLabel: "What you’ll explore",
    items: [
      "Characteristic clinical presentations.",
      "Relevant inflammatory findings.",
      "Follicular opening changes.",
      "Trichoscopic observations.",
      "Clinical comparison references.",
      "Educational case discussions.",
      "Biopsy and specialist referral considerations.",
      "Documentation of concerning findings.",
    ],
    result: "An organized educational atlas for studying six important scarring alopecia conditions.",
  },
  {
    numeral: "V",
    title: "The Alopecia Differential Diagnosis Roadmap™",
    headline: "Organize Your Clinical Reasoning With Structured Educational Decision Maps.",
    intro: [
      "Studying individual alopecia conditions is important.",
      "But understanding how different presentations may overlap is equally valuable.",
      "This volume focuses on educational differential diagnosis frameworks.",
    ],
    colorClass: "bg-vol-5",
    listLabel: "What you’ll explore",
    items: [
      "Diffuse hair loss comparison pathways.",
      "Patchy hair loss comparison pathways.",
      "Scarring versus non-scarring alopecia references.",
      "Inflammatory scalp findings.",
      "Trichoscopic comparison frameworks.",
      "Relevant clinical history considerations.",
      "Educational diagnostic decision maps.",
      "Clinical uncertainty documentation.",
      "Case-based differential diagnosis exercises.",
      "Factors requiring additional professional evaluation.",
    ],
    result: "A structured educational reference for organizing differential diagnosis considerations.",
  },
  {
    numeral: "VI",
    title: "The Alopecia Clinical Documentation System™",
    headline: "Bring Greater Structure to Your Assessment Records and Follow-Up Resources.",
    intro: [
      "A consistent documentation system helps organize relevant clinical information.",
      "This volume provides educational templates that can be adapted to appropriate professional settings.",
    ],
    colorClass: "bg-vol-6",
    listLabel: "What you’ll explore",
    items: [
      "Patient history templates.",
      "Hair loss assessment worksheets.",
      "Scalp examination documentation.",
      "Trichoscopy observation forms.",
      "Photographic documentation checklists.",
      "Differential diagnosis recording templates.",
      "Follow-up observation worksheets.",
      "Clinical case review forms.",
      "Patient communication records.",
      "Documentation quality checklists.",
    ],
    result:
      "A practical collection of educational documentation tools for organizing hair loss assessment information.",
  },
];
